import { useTranslation } from 'react-i18next';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import {
  AvailabilityBlockDialog,
  buildAvailabilityBlockDialogLabels,
} from './components/maintenance-block-dialog';
import type { AvailabilityBlockDialogValues } from './components/maintenance-block-dialog';
import {
  QueuePromoteDialog,
  buildPromoteCurrentSummary,
  buildPromoteNextSummary,
  buildQueuePromoteDialogLabels,
} from './components/queue-promote-dialog';
import type {
  PromoteCurrentSummarySource,
  PromoteNextSummarySource,
} from './components/queue-promote-dialog';
import { ReservationActionDialogs } from './components/reservation-action-dialogs';
import type { ActiveDialog } from './components/reservation-action-dialogs';
import { makeDialogLabels } from './lib/formatters';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface ActionDialogTarget {
  id: number;
  mediaUnitId: number;
  mediaUnitName: string;
  dateFrom: string;
  dateTo: string;
  effectiveExpiresAt?: string | null;
}

export interface PromoteDialogTarget {
  mediaUnitName: string;
  dateFrom: string;
  dateTo: string;
  current: PromoteCurrentSummarySource;
  next: PromoteNextSummarySource & { position: number };
}

export interface ReservationDialogsHostProps {
  action: {
    dialog: ActiveDialog;
    target: ActionDialogTarget | null;
    isAdmin: boolean;
    isPending: boolean;
    onClose: () => void;
    onConfirm: (action: string, dialogData?: Record<string, unknown>) => void;
  };
  availability: {
    isOpen: boolean;
    isPending: boolean;
    onClose: () => void;
    onSave: (values: AvailabilityBlockDialogValues) => void;
  };
  promote: {
    target: PromoteDialogTarget | null;
    isPending: boolean;
    onClose: () => void;
    onConfirm: () => void;
  };
}

export function ReservationDialogsHost({
  action,
  availability,
  promote,
}: ReservationDialogsHostProps) {
  const { t } = useTranslation('reservations');
  const translate = t as Translate;
  const { target } = action;
  const promoteTarget = promote.target;

  return (
    <>
      {target !== null ? (
        <ReservationActionDialogs
          activeDialog={action.dialog}
          reservationId={target.id}
          mediaUnitId={target.mediaUnitId}
          mediaUnitName={target.mediaUnitName}
          dateFrom={target.dateFrom}
          dateTo={target.dateTo}
          isAdmin={action.isAdmin}
          labels={makeDialogLabels(translate)}
          isPending={action.isPending}
          currentDeadline={target.effectiveExpiresAt}
          onClose={action.onClose}
          onConfirmAction={action.onConfirm}
        />
      ) : null}
      {availability.isOpen ? (
        <AvailabilityBlockDialog
          labels={buildAvailabilityBlockDialogLabels(t as (key: string) => string)}
          isPending={availability.isPending}
          onClose={availability.onClose}
          onSave={availability.onSave}
        />
      ) : null}
      {promoteTarget !== null ? (
        <QueuePromoteDialog
          mediaUnitName={promoteTarget.mediaUnitName}
          dateFrom={formatDisplayDate(promoteTarget.dateFrom)}
          dateTo={formatDisplayDate(promoteTarget.dateTo)}
          current={buildPromoteCurrentSummary(promoteTarget.current)}
          next={buildPromoteNextSummary(promoteTarget.next, t('queuePromoteDialog.newStatusLabel'))}
          nextPosition={promoteTarget.next.position}
          labels={buildQueuePromoteDialogLabels(translate)}
          isPending={promote.isPending}
          onClose={promote.onClose}
          onConfirm={promote.onConfirm}
        />
      ) : null}
    </>
  );
}
